export type AnalyticsValue =
	| string
	| number
	| boolean
	| null
	| undefined
	| string[]
	| number[];

export type AnalyticsProperties = Record<string, AnalyticsValue>;

export type AnalyticsEventName =
	| "$pageview"
	| "editor_entry_clicked"
	| "editor_load_failed"
	| "editor_opened"
	| "editor_session_ended"
	| "export_completed"
	| "media_import_completed"
	| "project_created";

type PostHogClient = typeof import("posthog-js").default;
type CaptureOptions = Parameters<PostHogClient["capture"]>[2];

interface QueuedEvent {
	eventName: AnalyticsEventName;
	properties: AnalyticsProperties;
	timestamp: Date;
}

// PostHog project tokens are public browser ingestion identifiers, not secrets.
// Keep these constants here so remote builds work without environment setup.
const PROJECT_TOKEN = "phc_BNd8TeQJh4BKDXvFnAsq6Gyo2QGitLknwhdELSEt7D9r";
const API_HOST = "https://us.i.posthog.com";
const MAX_QUEUED_EVENTS = 50;
const ATTRIBUTION_STORAGE_KEY = "editing_video_attribution_v1";
const SEND_BEACON_OPTIONS: CaptureOptions = { transport: "sendBeacon" };

let posthogPromise: Promise<PostHogClient | null> | null = null;
let posthogClient: PostHogClient | null = null;
let eventQueue: QueuedEvent[] = [];
let initializationScheduled = false;
let initialized = false;
let lastPageViewRoute: string | null = null;

export const POSTHOG_PERFORMANCE_SAFE_CONFIG = {
	autocapture: false,
	capture_pageview: false,
	capture_pageleave: false,
	capture_performance: false,
	capture_heatmaps: false,
	capture_dead_clicks: false,
	capture_exceptions: false,
	disable_session_recording: true,
	disable_surveys: true,
	disable_surveys_automatic_display: true,
	enable_recording_console_log: false,
	advanced_disable_flags: true,
	request_batching: true,
	person_profiles: "identified_only",
} as const;

export interface AnalyticsPage {
	pageName:
		| "home"
		| "projects"
		| "editor"
		| "about"
		| "privacy"
		| "terms"
		| "other";
	pageType: "marketing" | "project_library" | "editor" | "legal" | "other";
	routePath: string;
}

const LIVE_LOCALE_SEGMENTS = new Set(["en", "zh", "es", "pt"]);
const PROJECTS_ROUTE_SEGMENTS = new Set([
	"projects",
	"项目",
	"proyectos",
	"projetos",
]);
const EDITOR_ROUTE_SEGMENTS = new Set(["editor", "剪辑", "editar"]);
const ABOUT_ROUTE_SEGMENTS = new Set(["about", "关于", "acerca-de", "sobre"]);
const PRIVACY_ROUTE_SEGMENTS = new Set([
	"privacy",
	"隐私政策",
	"politica-de-privacidad",
	"politica-de-privacidade",
]);
const TERMS_ROUTE_SEGMENTS = new Set([
	"terms",
	"服务条款",
	"terminos-de-servicio",
	"termos-de-uso",
]);

function getPublicRouteSegments(pathname: string): string[] {
	let decodedPathname = pathname;
	try {
		decodedPathname = decodeURIComponent(pathname);
	} catch {
		// Keep malformed paths fail-open and classify them as other below.
	}

	const segments = decodedPathname.split("/").filter(Boolean);
	if (segments[0] && LIVE_LOCALE_SEGMENTS.has(segments[0])) {
		segments.shift();
	}
	return segments;
}

export function classifyAnalyticsPage(pathname: string): AnalyticsPage {
	const segments = getPublicRouteSegments(pathname);
	const firstSegment = segments[0];

	if (!firstSegment) {
		return { pageName: "home", pageType: "marketing", routePath: "/" };
	}
	if (PROJECTS_ROUTE_SEGMENTS.has(firstSegment) && segments.length === 1) {
		return {
			pageName: "projects",
			pageType: "project_library",
			routePath: "/projects",
		};
	}
	if (EDITOR_ROUTE_SEGMENTS.has(firstSegment) && segments.length >= 2) {
		return {
			pageName: "editor",
			pageType: "editor",
			routePath: "/editor/:project_id",
		};
	}
	if (ABOUT_ROUTE_SEGMENTS.has(firstSegment) && segments.length === 1) {
		return { pageName: "about", pageType: "marketing", routePath: "/about" };
	}
	if (PRIVACY_ROUTE_SEGMENTS.has(firstSegment) && segments.length === 1) {
		return { pageName: "privacy", pageType: "legal", routePath: "/privacy" };
	}
	if (TERMS_ROUTE_SEGMENTS.has(firstSegment) && segments.length === 1) {
		return { pageName: "terms", pageType: "legal", routePath: "/terms" };
	}
	return { pageName: "other", pageType: "other", routePath: pathname };
}

function isBrowser(): boolean {
	return typeof window !== "undefined" && typeof document !== "undefined";
}

function filterProperties(
	properties: AnalyticsProperties,
): AnalyticsProperties {
	return Object.fromEntries(
		Object.entries(properties).filter(([, value]) => value !== undefined),
	) as AnalyticsProperties;
}

interface StoredAttribution {
	firstLandingPage: string;
	firstReferringDomain?: string;
	firstUtmSource?: string;
	firstUtmMedium?: string;
	firstUtmCampaign?: string;
	lastUtmSource?: string;
	lastUtmMedium?: string;
	lastUtmCampaign?: string;
}

function isStoredAttribution(value: unknown): value is StoredAttribution {
	return (
		typeof value === "object" &&
		value !== null &&
		"firstLandingPage" in value &&
		typeof value.firstLandingPage === "string"
	);
}

function getReferringDomain(): string | undefined {
	if (!document.referrer) return undefined;
	try {
		return new URL(document.referrer).hostname || undefined;
	} catch {
		return undefined;
	}
}

function getAttributionProperties(): AnalyticsProperties {
	const query = new URLSearchParams(location.search);
	const currentUtm = {
		source: query.get("utm_source") || undefined,
		medium: query.get("utm_medium") || undefined,
		campaign: query.get("utm_campaign") || undefined,
	};
	let stored: StoredAttribution | null = null;

	try {
		const value = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
		if (value) {
			const parsed: unknown = JSON.parse(value);
			if (isStoredAttribution(parsed)) stored = parsed;
		}
	} catch {
		// Analytics remains fail-open when storage is unavailable.
	}

	const page = classifyAnalyticsPage(location.pathname);
	const attribution: StoredAttribution = stored ?? {
		firstLandingPage: page.routePath,
		firstReferringDomain: getReferringDomain(),
		firstUtmSource: currentUtm.source,
		firstUtmMedium: currentUtm.medium,
		firstUtmCampaign: currentUtm.campaign,
	};

	if (currentUtm.source || currentUtm.medium || currentUtm.campaign) {
		attribution.lastUtmSource = currentUtm.source;
		attribution.lastUtmMedium = currentUtm.medium;
		attribution.lastUtmCampaign = currentUtm.campaign;
	}

	try {
		localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
	} catch {
		// Analytics remains fail-open when storage is unavailable.
	}

	return filterProperties({
		first_landing_page: attribution.firstLandingPage,
		first_referring_domain: attribution.firstReferringDomain,
		first_utm_source: attribution.firstUtmSource,
		first_utm_medium: attribution.firstUtmMedium,
		first_utm_campaign: attribution.firstUtmCampaign,
		last_utm_source: attribution.lastUtmSource,
		last_utm_medium: attribution.lastUtmMedium,
		last_utm_campaign: attribution.lastUtmCampaign,
	});
}

function getGlobalProperties(): AnalyticsProperties {
	const page = classifyAnalyticsPage(location.pathname);
	return filterProperties({
		app: "editing.video",
		environment: process.env.NODE_ENV,
		locale: document.documentElement.lang || navigator.language,
		page_name: page.pageName,
		page_type: page.pageType,
		route_path: page.routePath,
		page_url: `${location.origin}${page.routePath}`,
		referring_domain: getReferringDomain(),
		...getAttributionProperties(),
	});
}

function flushQueue(client: PostHogClient): void {
	for (const event of eventQueue) {
		client.capture(event.eventName, event.properties, {
			timestamp: event.timestamp,
		});
	}
	eventQueue = [];
}

async function initializePostHog(): Promise<PostHogClient | null> {
	if (!isBrowser() || !PROJECT_TOKEN) return null;
	if (posthogClient) return posthogClient;
	if (!posthogPromise) {
		posthogPromise = import("posthog-js")
			.then((module) => {
				const client = module.default;
				if (!initialized) {
					client.init(PROJECT_TOKEN, {
						api_host: API_HOST,
						...POSTHOG_PERFORMANCE_SAFE_CONFIG,
					});
					initialized = true;
				}
				posthogClient = client;
				flushQueue(client);
				return client;
			})
			.catch(() => {
				posthogPromise = null;
				return null;
			});
	}
	return posthogPromise;
}

function scheduleAfterPageLoad(callback: () => void): void {
	const scheduleIdle = () => {
		if (window.requestIdleCallback) {
			window.requestIdleCallback(callback, { timeout: 4_000 });
			return;
		}
		window.setTimeout(callback, 1_500);
	};

	if (document.readyState === "complete") {
		scheduleIdle();
		return;
	}
	window.addEventListener("load", scheduleIdle, { once: true });
}

export function scheduleAnalyticsInitialization(): void {
	if (!isBrowser() || initializationScheduled || !PROJECT_TOKEN) return;
	initializationScheduled = true;
	scheduleAfterPageLoad(() => void initializePostHog());
}

export function analyticsTrack({
	eventName,
	properties = {},
}: {
	eventName: AnalyticsEventName;
	properties?: AnalyticsProperties;
}): void {
	if (!isBrowser()) return;
	const event: QueuedEvent = {
		eventName,
		properties: { ...getGlobalProperties(), ...filterProperties(properties) },
		timestamp: new Date(),
	};

	if (posthogClient) {
		posthogClient.capture(event.eventName, event.properties, {
			timestamp: event.timestamp,
		});
		return;
	}

	eventQueue = [...eventQueue, event].slice(-MAX_QUEUED_EVENTS);
	scheduleAnalyticsInitialization();
}

export function analyticsTrackImmediate({
	eventName,
	properties = {},
}: {
	eventName: AnalyticsEventName;
	properties?: AnalyticsProperties;
}): void {
	if (!isBrowser()) return;
	const eventProperties = {
		...getGlobalProperties(),
		...filterProperties(properties),
	};
	if (posthogClient) {
		posthogClient.capture(eventName, eventProperties, SEND_BEACON_OPTIONS);
		return;
	}
	analyticsTrack({ eventName, properties });
}

export function analyticsPageView(pathname: string): void {
	const page = classifyAnalyticsPage(pathname);
	if (lastPageViewRoute === page.routePath) return;
	lastPageViewRoute = page.routePath;
	analyticsTrack({
		eventName: "$pageview",
		properties: {
			$current_url: `${location.origin}${page.routePath}`,
			$pathname: page.routePath,
		},
	});
}
